/**
 * Add-ons step: every toggle renders FROM THE G1 REGISTRY — labels, value
 * kinds, and count bounds all arrive over /api/service-descriptors; the
 * client hardcodes nothing (no €25, no 0-5 cap). Visibility comes from the
 * service descriptor's allowedAddOns keys. Whole rows (≥56px) are the touch
 * target. Every change re-prices live via the seq-guarded quote fetch;
 * prices come from the response lines only.
 */
import { Button } from '@ecmb/ui';
import type { JobState, OperatorStop, QuoteOptions } from '../store/state.js';
import { addOnsForService, type Registry } from '../store/registry.js';
import { TotalBar } from '../components/TotalBar.js';
import { DriverDetails, type DriverEndPatch } from '../components/DriverDetails.js';

export type AddonsProps = {
  registry: Registry;
  service: string;
  /** T400 — the driver-details block reads/writes job capture fields. */
  job: JobState;
  options: QuoteOptions;
  totalCents: number | null;
  estimated: boolean;
  /**
   * T660 M6 P1-5 — a stepper tap re-prices, and this screen keeps showing the
   * PREVIOUS total while that is in flight. The bar's live region holds its
   * announcement until the re-price settles, so a burst of taps reads out once.
   */
  quotePending: boolean;
  onChange: (key: keyof QuoteOptions, value: boolean | number) => void;
  onSetEnd: (patch: DriverEndPatch) => void;
  onSetStop: (index: number, patch: Partial<OperatorStop>) => void;
  onSeePrice: () => void;
};

export function Addons(props: AddonsProps) {
  const addons = addOnsForService(props.registry, props.service);
  // T400 item 3 — the customer's stated stops, visible at the exact moment
  // Helen decides the billable count (kills the "operator saw Extra pickups 0"
  // class). Display-only: the count is never derived from the statement.
  const stated = props.job.customerStops;
  return (
    <div class="screen-pane screen-pane--with-bar">
      <h1 class="screen-title" tabIndex={-1}>Anything extra?</h1>
      {stated.length > 0 && (
        <p class="field-hint" role="note">
          {`Customer listed ${stated.length} ${stated.length === 1 ? 'stop' : 'stops'}: ${stated
            .map((s) => (s.eircode ? `${s.address} (${s.eircode})` : s.address))
            .join(', ')}`}
        </p>
      )}
      <div class="toggle-list">
        {addons.map((a) => {
          const key = a.engineKey as keyof QuoteOptions;
          if (a.valueKind === 'count') {
            const bounds = a.bounds ?? { min: 0, max: 0 };
            const value = Number(props.options[key] ?? bounds.min);
            return (
              <div key={a.engineKey} class="toggle-row" role="group" aria-label={a.label}>
                <span class="toggle-label">{a.label}</span>
                {/* ⭐⭐ T1749 M9 (DS-26) — `Button`, AND `ariaDisabled` AT THE
                    BOUND RATHER THAN THE NATIVE ATTRIBUTE. The M3 webapp pass
                    measured what `disabled` costs here: the attribute takes the
                    control out of the tab order and out of the accessibility
                    tree, so a keyboard user at `0 extra pickups` could not land
                    on the minus, could not hear that it was unavailable, and had
                    no way to tell "no lower" from "gone". `Button` withholds
                    `onClick`, cancels Space and Enter and cancels the pointer
                    default (`Button.tsx:219-262`), so the control refuses exactly
                    as hard while staying reachable and announcing itself.
                    ⛔ NEVER `disabled` ON THESE TWO — asserted, and planted-red
                    proven, in `quote-flow.spec.tsx`.
                    ⛔ AND THE NAMES STAY TEMPLATES OVER THE REGISTRY LABEL. They
                    read `Fewer extra pickups` today because that is what
                    `/api/service-descriptors` calls the add-on; writing the two
                    strings out as literals would quietly enter them into the
                    locked-name contract and make a registry rename a test
                    failure on a screen that hardcodes nothing (this file's own
                    header rule). */}
                <span class="stepper">
                  <Button
                    variant="quiet"
                    size="inline"
                    ariaLabel={`Fewer ${a.label.toLowerCase()}`}
                    ariaDisabled={value <= bounds.min}
                    onClick={() => props.onChange(key, value - 1)}
                  >
                    −
                  </Button>
                  <span class="stepper-value" aria-live="polite">{value}</span>
                  <Button
                    variant="quiet"
                    size="inline"
                    ariaLabel={`More ${a.label.toLowerCase()}`}
                    ariaDisabled={value >= bounds.max}
                    onClick={() => props.onChange(key, value + 1)}
                  >
                    +
                  </Button>
                </span>
              </div>
            );
          }
          const on = Boolean(props.options[key]);
          return (
            <button
              key={a.engineKey}
              type="button"
              class={`toggle-row toggle-row--tappable${on ? ' toggle-row--on' : ''}`}
              role="switch"
              aria-checked={on}
              onClick={() => props.onChange(key, !on)}
            >
              <span class="toggle-label">{a.label}</span>
              <span class="toggle-knob" aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {/* T400 — capture where the count lives: the per-stop rows appear the
          moment Helen sets extra pickups > 0, pre-seeded from the statement. */}
      <DriverDetails
        job={props.job}
        showStops={true}
        // P0-28 — the four per-end address inputs sat between the toggles and
        // See Price and read as fields that must be filled (a 29-second dwell,
        // then a tap past them untouched). Behind a disclosure here, and ONLY
        // here: the Quote mounts stay as they are.
        collapsible={true}
        onSetEnd={props.onSetEnd}
        onSetStop={props.onSetStop}
      />
      <TotalBar
        label={props.estimated ? 'Est.' : 'Total'}
        totalCents={props.totalCents}
        pending={props.quotePending}
        primaryLabel="See Price"
        onPrimary={props.onSeePrice}
      />
    </div>
  );
}
